import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik"; // Added useFormikContext
import * as Yup from "yup";
import SidePanel from "layouts/SidePanel";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../../utils/axios";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";

const validationSchema = Yup.object().shape({
    poItems: Yup.array().of(
        Yup.object().shape({
            vendorName: Yup.string().required("Required"),
            poNumber: Yup.string()
                .required("Required")
                .matches(/^[a-zA-Z0-9]+$/, "Invalid"),
            poAmount: Yup.string()
                .required("Required")
                .matches(/^\d+(\.\d{1,2})?$/, "Invalid"),
            createdByName: Yup.string().required("Required")
                .required("Required")
                .matches(/^[a-zA-Z\s.,]*$/, "Invalid"),
            createdOn: Yup.date().required("Required"),
        })
    ),
});

const AddPoForm = () => {
    const location = useLocation();
    const { rfqData } = location.state || {};

    // Filter suppliers who have submitted quotations
    const vendorsWithQuotations = rfqData?.suppliers?.filter(
        supplier => supplier.quotationReceived === true
    ) || [];

    // Extract vendor names for the dropdown
    const vendorOptions = vendorsWithQuotations.map(supplier => ({
        value: supplier.supplierId,
        label: supplier.businessName
    }));

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth);
    const user = auth?.user;
    const [loading, setLoading] = useState(false);
    const [editableRows, setEditableRows] = useState({});
    const dropdownRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCreatedByIndex, setActiveCreatedByIndex] = useState(null);
    const [vendorSearchTerm, setVendorSearchTerm] = useState("");
    const [activeVendorIndex, setActiveVendorIndex] = useState(null);
    const vendorDropdownRef = useRef(null);

    const filteredVendorOptions = vendorOptions.filter((vendor) =>
        vendor.label.toLowerCase().includes(vendorSearchTerm.toLowerCase())
    );


    // from Formik
    // const { values, setFieldValue } = useFormikContext();

    // prepare team options for current user
    const allCreatedByOptions = [
        user?.name,
        ...(user?.teamMembers?.map((m) => m.name) || []),
    ];
    const filteredCreatedByOptions = allCreatedByOptions.filter((name) =>
        name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const createdByDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) &&
                (createdByDropdownRef.current && !createdByDropdownRef.current.contains(event.target))
            ) {
                setActiveCreatedByIndex(null);
                setActiveVendorIndex(null);
                setSearchTerm("");
                setVendorSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const handleBack = () => {
        navigate(-1);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);

            const payload = {
                rfqId: rfqData?.rfqId,
                poItems: values.poItems.map(item => ({
                    supplierId: item.supplierId,
                    vendorName: item.vendorName,
                    poNumber: item.poNumber,
                    poAmount: item.poAmount,
                    createdByName: item.createdByName,
                    createdOn: item.createdOn,
                    remarks: item.remarks,
                    grnDetails: item.grnDetails.map(grn => ({
                        grnNumber: grn.grnNumber,
                        grnDate: grn.grnDate,
                        grnComments: grn.grnComments || ""
                    }))
                }))
            };

            const response = await axios.post("/api/rfqs/add/po", payload);

            if (response.data?.error) {
                enqueueSnackbar(response.data.message || "Failed to map PO", { variant: "error", autoHideDuration: 6000 });
            } else {
                enqueueSnackbar("PO(s) mapped successfully", { variant: "success", autoHideDuration: 6000 });
                navigate(-1);
            }
        } catch (error) {
            const duplicatePoNumbers = error?.response?.data?.duplicatePoNumbers;

            if (duplicatePoNumbers && duplicatePoNumbers.length > 0) {
                enqueueSnackbar(
                    `Duplicate PO number(s) found: ${duplicatePoNumbers.join(", ")}`,
                    { variant: "error", autoHideDuration: 6000 }
                );
            } else {
                enqueueSnackbar("Something went wrong while creating PO(s)", { variant: "error", autoHideDuration: 6000 });
            }

            console.error("PO creation error:", error);
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };


    return (
        <SidePanel>
            <div className="text-sm mx-auto p-6 bg-white">
                <div className="flex justify-between">
                    <h2 className="text-xl col-span-3 font-medium mb-4">
                        {rfqData?.rfqType === "material" ? "Add Purchase Order" : "Add Work Order"}
                    </h2>
                    {/* <button
                        onClick={handleBack}
                        className="mb-4 px-4 py-1 col-span-2 w-fit hover:bg-gray-100 border border-black"
                    >
                        Back
                    </button> */}
                </div>

                <div className="border-black border space-y-4 p-4">
                    <Formik
                        initialValues={{
                            rfqId: rfqData?.rfqId || "",
                            poItems: [{
                                vendorName: "",
                                supplierId: "",
                                poNumber: "",
                                poAmount: "",
                                createdByName: user?.name || "",
                                createdOn: "",
                                remarks: "",
                                grnDetails: [] 
                            }]
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, isSubmitting }) => (
                            <Form>
                                <div className="flex items-center justify-center w-full">
                                    <div className="bg-gray-500 bg-opacity-50 w-full">
                                        <div className="border border-gray-300 bg-white w-full">
                                            {/* Table Header */}
                                            <div className="bg-gray-100 grid grid-cols-26 gap-1 text-sm text-[#777777] px-2 pt-3 pb-2">
                                                <p className="text-center col-span-1">Sr#</p>
                                                <p className="text-center col-span-5">Vendor Name
                                                    <span className="text-red-500">*</span></p>
                                                <p className="text-center col-span-3">
                                                    {rfqData?.rfqType === "material" ? "PO Number" : "WO Number"}
                                                    <span className="text-red-500">*</span>
                                                </p>
                                                <p className="text-center col-span-3">
                                                    {rfqData?.rfqType === "material" ? "Amount(Excl.GST)" : "Amount(Excl.GST)"}
                                                    <span className="text-red-500">*</span>
                                                </p>
                                                <p className="text-center col-span-4">Created By
                                                    <span className="text-red-500">*</span>
                                                </p>
                                                <p className="text-center col-span-3">
                                                    {rfqData?.rfqType === "material" ? "PO Date" : "WO Date"}
                                                    <span className="text-red-500">*</span>
                                                </p>
                                                <p className="text-center col-span-7">Remark</p>
                                            </div>


                                            {/* Table Rows */}
                                            <div className="p-2 text-sm">
                                                {values.poItems.map((poItem, index) => (
                                                    <React.Fragment key={index}>
                                                        {index > 0 && <hr className="my-4 border-gray-300" />} {/* Add horizontal line between PO rows */}

                                                        <div className="grid grid-cols-26 gap-1 items-start mb-2">
                                                            {/* Serial Number */}
                                                            <div className="flex items-center justify-center col-span-1">
                                                                {index + 1}
                                                            </div>

                                                            {/* Vendor Name */}
                                                            <div className="col-span-5 relative" ref={vendorDropdownRef}>
                                                                <input
                                                                    type="text"
                                                                    className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-2 h-[28px] w-full text-xs font-normal"
                                                                    placeholder="Select a vendor"
                                                                    value={values.poItems[index]?.vendorName || ""}
                                                                    onChange={(e) => {
                                                                        const inputValue = e.target.value;
                                                                        setVendorSearchTerm(inputValue);

                                                                        const matchedVendor = vendorOptions.find(
                                                                            (v) => v.label.toLowerCase() === inputValue.toLowerCase()
                                                                        );

                                                                        if (matchedVendor) {
                                                                            setFieldValue(`poItems.${index}.supplierId`, matchedVendor.value);
                                                                            setFieldValue(`poItems.${index}.vendorName`, matchedVendor.label);
                                                                        } else {
                                                                            setFieldValue(`poItems.${index}.supplierId`, "");
                                                                            setFieldValue(`poItems.${index}.vendorName`, inputValue);
                                                                        }

                                                                        setActiveVendorIndex(index);
                                                                    }}
                                                                    onFocus={() => setActiveVendorIndex(index)}
                                                                    onBlur={(e) => {
                                                                        setTimeout(() => {
                                                                            if (!vendorDropdownRef.current.contains(document.activeElement)) {
                                                                                setActiveVendorIndex(null);
                                                                            }
                                                                        }, 100);
                                                                    }}
                                                                />

                                                                {/* Dropdown icon */}
                                                                <img
                                                                    src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_dropdown_arrow.svg"
                                                                    alt="toggle"
                                                                    className={`w-2 h-2 cursor-pointer bg-white absolute right-1 top-1/2 transform -translate-y-1/2 transition-transform duration-200 ${activeVendorIndex === index ? "rotate-180" : "rotate-0"
                                                                        }`}
                                                                    onClick={() =>
                                                                        setActiveVendorIndex(activeVendorIndex === index ? null : index)
                                                                    }
                                                                />

                                                                {/* Dropdown List */}
                                                                {activeVendorIndex === index && (
                                                                    <ul className="absolute z-20 w-full font-normal bg-white border max-h-[130px] overflow-y-auto mt-1 shadow-lg text-xs">
                                                                        {filteredVendorOptions.length > 0 ? (
                                                                            filteredVendorOptions.map((vendor, i) => (
                                                                                <li
                                                                                    key={vendor.value}
                                                                                    tabIndex={-1}
                                                                                    onMouseDown={(e) => {
                                                                                        e.preventDefault();
                                                                                        setFieldValue(`poItems.${index}.supplierId`, vendor.value);
                                                                                        setFieldValue(`poItems.${index}.vendorName`, vendor.label);
                                                                                        setVendorSearchTerm(vendor.label);
                                                                                        setActiveVendorIndex(null);
                                                                                    }}
                                                                                    className="p-2 cursor-pointer hover:bg-gray-100"
                                                                                >
                                                                                    {vendor.label}
                                                                                </li>
                                                                            ))
                                                                        ) : (
                                                                            <li className="p-2 text-gray-500">No matching results</li>
                                                                        )}
                                                                    </ul>
                                                                )}

                                                                <ErrorMessage
                                                                    name={`poItems.${index}.supplierId`}
                                                                    component="div"
                                                                    className="text-red-500 text-xs mt-1"
                                                                />
                                                            </div>

                                                            {/* PO Number */}
                                                            <div className="col-span-3">
                                                                <Field name={`poItems.${index}.poNumber`}>
                                                                    {({ field }) => (
                                                                        <input
                                                                            {...field}
                                                                            type="text"
                                                                            className="w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                const regex = /^[a-zA-Z0-9]*$/;
                                                                                if (value === "" || regex.test(value)) {
                                                                                    field.onChange(e);
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                <ErrorMessage
                                                                    name={`poItems.${index}.poNumber`}
                                                                    component="div"
                                                                    className="text-red-500 text-xs"
                                                                />
                                                            </div>

                                                            {/* PO Amount */}
                                                            <div className="col-span-3">
                                                                <Field name={`poItems.${index}.poAmount`}>
                                                                    {({ field }) => (
                                                                        <input
                                                                            {...field}
                                                                            type="text"
                                                                            className="w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                const regex = /^\d*\.?\d{0,2}$/;
                                                                                if (value === "" || regex.test(value)) {
                                                                                    field.onChange(e);
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                <ErrorMessage
                                                                    name={`poItems.${index}.poAmount`}
                                                                    component="div"
                                                                    className="text-red-500 text-xs"
                                                                />
                                                            </div>

                                                            {/* Created By */}
                                                            <div className="col-span-4 relative" ref={createdByDropdownRef}>
                                                                {(user?.teamMembers && user.teamMembers.length > 0) ? (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-2 h-[28px] w-full text-xs font-normal"
                                                                            placeholder="Select creator"
                                                                            value={values.poItems[index]?.createdByName || ""}
                                                                            onChange={(e) => {
                                                                                const inputValue = e.target.value;
                                                                                setSearchTerm(inputValue);
                                                                                setFieldValue(`poItems.${index}.createdByName`, inputValue);
                                                                            }}
                                                                            onFocus={() => {
                                                                                setSearchTerm("");
                                                                                setActiveCreatedByIndex(index);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                setTimeout(() => {
                                                                                    if (!createdByDropdownRef.current.contains(document.activeElement)) {
                                                                                        setActiveCreatedByIndex(null);
                                                                                    }
                                                                                }, 100);
                                                                            }}
                                                                        />

                                                                        <img
                                                                            src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_dropdown_arrow.svg"
                                                                            alt="toggle"
                                                                            className={`w-2 h-2 cursor-pointer bg-white absolute right-1 top-1/2 transform -translate-y-1/2 transition-transform duration-200 ${activeCreatedByIndex === index ? "rotate-180" : "rotate-0"}`}
                                                                            onClick={() => {
                                                                                setSearchTerm("");
                                                                                setActiveCreatedByIndex(activeCreatedByIndex === index ? null : index);
                                                                            }}
                                                                        />

                                                                        {activeCreatedByIndex === index && (
                                                                            <ul className="absolute z-20 w-full font-normal bg-white border max-h-[130px] overflow-y-auto mt-1 shadow-lg text-xs">
                                                                                {filteredCreatedByOptions.length > 0 ? (
                                                                                    filteredCreatedByOptions.map((option, i) => (
                                                                                        <li
                                                                                            key={i}
                                                                                            tabIndex={-1}
                                                                                            onMouseDown={(e) => {
                                                                                                e.preventDefault();
                                                                                                setFieldValue(`poItems.${index}.createdByName`, option);
                                                                                                setSearchTerm("");
                                                                                                setActiveCreatedByIndex(null);
                                                                                            }}
                                                                                            className="p-2 cursor-pointer hover:bg-gray-100"
                                                                                        >
                                                                                            {option}
                                                                                        </li>
                                                                                    ))
                                                                                ) : (
                                                                                    <li className="p-2 text-sm text-gray-500">
                                                                                        No matching results
                                                                                    </li>
                                                                                )}
                                                                            </ul>
                                                                        )}

                                                                        <ErrorMessage
                                                                            name={`poItems.${index}.createdByName`}
                                                                            component="div"
                                                                            className="text-red-500 text-xs mt-1"
                                                                        />
                                                                    </>) : (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            className="border border-[#DCDCD0] bg-gray-100 p-2 h-[28px] w-full text-xs font-normal text-gray-500"
                                                                            value={user?.name || ""}
                                                                            placeholder="Creator not available"
                                                                            disabled
                                                                        />
                                                                        <Field
                                                                            name={`poItems.${index}.createdByName`}
                                                                            type="hidden"
                                                                            value={user?.name || ""}
                                                                        />
                                                                    </>
                                                                )}
                                                            </div>

                                                            {/* Created On */}
                                                            <div className="col-span-3">
                                                                <Field
                                                                    name={`poItems.${index}.createdOn`}
                                                                    type="date"
                                                                    className="w-full border border-gray-300 px-2 py-1 text-xs focus:ring-0"
                                                                />
                                                                <ErrorMessage
                                                                    name={`poItems.${index}.createdOn`}
                                                                    component="div"
                                                                    className="text-red-500 text-xs"
                                                                />
                                                            </div>

                                                            {/* Remarks */}
                                                            <div className="col-span-7 flex items-center space-x-2">
                                                                <ReactQuill
                                                                    value={poItem.remarks || ""}
                                                                    onChange={(value) =>
                                                                        setFieldValue(`poItems.${index}.remarks`, value)
                                                                    }
                                                                    theme="snow"
                                                                    className="min-h-[30px] max-h-[100px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
                                                                    modules={{ toolbar: false }}
                                                                />
                                                                <div className="col-span-1 flex items-center justify-evenly">
                                                                    {/* <button
                                                                        type="button"
                                                                        className="w-fit text-lg"
                                                                        onClick={() => {
                                                                            setVendorSearchTerm("")
                                                                            const newPoItem = {
                                                                                vendorName: "",
                                                                                poNumber: "",
                                                                                poAmount: "",
                                                                                createdByName: user?.name || "",
                                                                                createdOn: "",
                                                                                remarks: "",
                                                                                grnDetails: []
                                                                            };
                                                                            setFieldValue("poItems", [...values.poItems, newPoItem]);
                                                                        }}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
                                                                        </svg>
                                                                    </button> */}
                                                                    {values.poItems.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const updatedPoItems = values.poItems.filter((_, i) => i !== index);
                                                                                setFieldValue("poItems", updatedPoItems);
                                                                            }}
                                                                            className="text-lg"
                                                                        >
                                                                            {/* <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                                                                            </svg> */}
                                                                            <svg
                                                                                className="w-5 h-5"
                                                                                focusable="false"
                                                                                aria-hidden="true"
                                                                                viewBox="0 0 24 24"
                                                                                tabIndex="-1"
                                                                                title="Delete Section"
                                                                            >
                                                                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12 1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z"></path>
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* GRN Details Section - Always shown but starts empty */}
                                                        <div className="mt-4 mb-6">
                                                            <div className="grid grid-cols-26 gap-1">
                                                                <div className="col-span-1"></div>
                                                                <p className="col-span-3 text-left text-sm font-medium text-gray-700">
                                                                    GRN Details
                                                                    {/* for PO: {poItem.poNumber || `PO #${index + 1}`} (Optional) */}
                                                                </p>
                                                            </div>

                                                            <div className="bg-gray-500 bg-opacity-50 w-full">
                                                                <div className="bg-white w-full">
                                                                    {/* GRN Table Header */}
                                                                    <div className="grid grid-cols-26 gap-1 pb-2 pt-3 text-sm text-[#777777]">
                                                                        <p className="col-span-1"></p>
                                                                        <p className="text-left col-span-1">Sr#</p>
                                                                        <p className="text-center col-span-4">GRN No.</p>
                                                                        <p className="text-center col-span-3">GRN Date</p>
                                                                        <p className="text-center col-span-7">GRN Comments</p>
                                                                        <p className="text-center col-span-2"></p>
                                                                    </div>

                                                                    {/* GRN Table Rows */}
                                                                    <div className="text-sm">
                                                                        {/* Always show at least one row */}
                                                                        {(poItem.grnDetails && poItem.grnDetails.length > 0 ? poItem.grnDetails : [{ grnNumber: "", grnDate: "" }])
                                                                            .map((grnItem, grnIndex) => (
                                                                                <div key={grnIndex} className="grid grid-cols-26 gap-1 items-start mb-2">
                                                                                    <div className="col-span-1"></div>
                                                                                    <div className="text-left col-span-1">
                                                                                        {index + 1}.{grnIndex + 1}
                                                                                    </div>
                                                                                    <div className="col-span-4">
                                                                                        <Field
                                                                                            name={`poItems.${index}.grnDetails.${grnIndex}.grnNumber`}
                                                                                            type="text"
                                                                                            className="w-full border border-gray-300 px-2 py-1 text-xs focus:ring-0"
                                                                                        // placeholder="Enter GRN number"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-span-3">
                                                                                        <Field
                                                                                            name={`poItems.${index}.grnDetails.${grnIndex}.grnDate`}
                                                                                            type="date"
                                                                                            className="w-full border border-gray-300 px-2 py-1 text-xs focus:ring-0"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-span-7">
                                                                                        <ReactQuill
                                                                                            value={grnItem.grnComments || ""}
                                                                                            onChange={(value) =>
                                                                                                setFieldValue(`poItems.${index}.grnDetails.${grnIndex}.grnComments`, value)
                                                                                            }
                                                                                            theme="snow"
                                                                                            className="min-h-[28px] max-h-[80px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
                                                                                            modules={{ toolbar: false }}
                                                                                        />
                                                                                    </div>

                                                                                    <div className="col-span-1 flex items-center mx-2 mt-1 gap-x-2">
                                                                                        {/* Always show + button for all rows */}
                                                                                        <button
                                                                                            type="button"
                                                                                            className="w-fit text-lg"
                                                                                            onClick={() => {
                                                                                                const currentGrnDetails = poItem.grnDetails || [];
                                                                                                const newGrnDetails = [...currentGrnDetails];
                                                                                                newGrnDetails.splice(grnIndex + 1, 0, {
                                                                                                    grnNumber: "",
                                                                                                    grnDate: "",
                                                                                                    grnComments: "" // Add this if you're including comments
                                                                                                });
                                                                                                setFieldValue(`poItems.${index}.grnDetails`, newGrnDetails);
                                                                                            }}
                                                                                        >
                                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
                                                                                            </svg>
                                                                                        </button>

                                                                                        {/* Show - button only if there is more than one row */}
                                                                                        {((poItem.grnDetails && poItem.grnDetails.length > 1) ||
                                                                                            (!poItem.grnDetails && grnIndex > 0) ||
                                                                                            (poItem.grnDetails && poItem.grnDetails.length === 0 && grnIndex > 0)) && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        const currentGrnDetails = poItem.grnDetails || [];
                                                                                                        if (currentGrnDetails.length === 0) {
                                                                                                            // If showing default empty rows (not in array yet)
                                                                                                            // Just clear the fields for this row
                                                                                                            setFieldValue(`poItems.${index}.grnDetails.${grnIndex}.grnNumber`, "");
                                                                                                            setFieldValue(`poItems.${index}.grnDetails.${grnIndex}.grnDate`, "");
                                                                                                            setFieldValue(`poItems.${index}.grnDetails.${grnIndex}.grnComments`, "");
                                                                                                        } else {
                                                                                                            // Remove the row from the array
                                                                                                            const newGrnDetails = currentGrnDetails.filter((_, i) => i !== grnIndex);
                                                                                                            setFieldValue(`poItems.${index}.grnDetails`, newGrnDetails);
                                                                                                        }
                                                                                                    }}
                                                                                                    className="text-lg"
                                                                                                >
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                                                                                                    </svg>
                                                                                                </button>
                                                                                            )}
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                                        <button
                                                            type="button"
                                                            className="text-sm border border-black rounded-sm px-3 py-1 ml-2 mb-2"
                                                            onClick={() => {
                                                                setVendorSearchTerm("")
                                                                const newPoItem = {
                                                                    vendorName: "",
                                                                    poNumber: "",
                                                                    poAmount: "",
                                                                    createdByName: user?.name || "",
                                                                    createdOn: "",
                                                                    remarks: "",
                                                                    grnDetails: []
                                                                };
                                                                setFieldValue("poItems", [...values.poItems, newPoItem]);
                                                            }}
                                                        >
                                                            Add PO
                                                        </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-end space-x-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="px-4 py-1 w-fit hover:bg-gray-100 border border-black"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || loading}
                                        className={`px-4 py-1 ${isSubmitting || loading
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-blue-500 hover:bg-blue-600"
                                            } text-white`}
                                    >
                                        {isSubmitting || loading ? "Submitting..." : "Save"}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </SidePanel>
    );
};

export default AddPoForm;